import React, { useState, useEffect } from 'react'
import './styles.css'
import Form from 'react-bootstrap/Form'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts'
import { flatten, debounce } from 'lodash'
import cn from 'classnames'
import { injectIntl } from 'react-intl'
import Modal from 'components/Modal'
import Button from '../Button'
import LoadingSpinner from '../LoadingSpinner'

const LOCAL = 'LOCAL'
const REMOTE = 'REMOTE'
const AUDIO = 'AUDIO'
const VIDEO = 'VIDEO'

const DEFAULT_STATS_TO_SHOW = {
  [`${LOCAL}_${AUDIO}`]: ['packetsSent', 'packetsLost', 'bytesSent', 'roundTripTime', 'audioLevel', 'jitter'],
  [`${REMOTE}_${AUDIO}`]: ['packetsSent', 'packetsLost', 'bytesSent', 'audioLevel', 'jitter'],
  [`${LOCAL}_${VIDEO}`]: ['packetsSent', 'packetsLost', 'bytesSent', 'roundTripTime', 'frameRate', 'dimensionsWidth', 'dimensionsHeight'],
  [`${REMOTE}_${VIDEO}`]: ['packetsSent', 'packetsLost', 'bytesSent', 'frameRate', 'dimensionsWidth', 'dimensionsHeight'],
}
const STATS_EXCLUSIONS = ['timestamp', 'formattedDate', 'formattedTime', 'codec', 'dimensions', 'captureDimensions']

const WebRTCStats = ({ statsData, statusCode, loading, getWebRtcStats, intl }) => {
  const [selectedPeerId, setSelectedPeerId] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [searched, setSearched] = useState(false)
  const [configureModalProps, setConfigureModalProps] = useState({ dataKey: '', show: false})
  const [availableStats, setAvailableStats] = useState([])
  const [selectedStats, setSelectedStats] = useState([])
  const [statsToShow, setStatsToShow] = useState(DEFAULT_STATS_TO_SHOW)
  const [sectionsToShow, setSectionsToShow] = useState(Object.keys(DEFAULT_STATS_TO_SHOW))
  const prepareTrackStats = trackStats => {
    return flatten(trackStats).map(el => {
      const result =  {
        ...el,
        formattedDate: intl.formatDate(el.timestamp, { year: 'numeric', month: 'short', day: 'numeric',}),
        formattedTime: intl.formatTime(el.timestamp, { hour: 'numeric', minute: 'numeric', second: 'numeric'})
      }
      if (el.dimensions) {
        el.dimensionsWidth = el.dimensions.width
        el.dimensionsHeight = el.dimensions.height
      }
      if (el.captureDimensions) {
        el.captureDimensionsWidth = el.captureDimensions.width
        el.captureDimensionsHeight = el.captureDimensions.height
      }
      return result
    })
  }

  useEffect(() => {
    setSelectedPeerId(statsData && Object.keys(statsData).length && Object.keys(statsData)[0])
  }, [statsData])

  const selectedData = statsData && statsData[selectedPeerId] || []
  const localAudioData = prepareTrackStats(selectedData.map(data => data.localAudioTrackStats))
  const localVideoData = prepareTrackStats(selectedData.map(data => data.localVideoTrackStats))
  const remoteAudioData = prepareTrackStats(selectedData.map(data => data.remoteAudioTrackStats))
  const remoteVideoData = prepareTrackStats(selectedData.map(data => data.remoteVideoTrackStats))
  const date = localAudioData.length && localAudioData[0].formattedDate
  const peerConnectionId = localAudioData.length && selectedData[0].peerConnectionId

  const dataByType = {
    [`${LOCAL}_${AUDIO}`]: {
      data: localAudioData,
      codec: localAudioData.length && localAudioData[0].codec
    },
    [`${REMOTE}_${AUDIO}`]: {
      data: remoteAudioData,
      codec: remoteAudioData.length && remoteAudioData[0].codec
    },
    [`${LOCAL}_${VIDEO}`]: {
      data: localVideoData,
      codec: localVideoData.length && localVideoData[0].codec
    },
    [`${REMOTE}_${VIDEO}`]: {
      data: remoteVideoData,
      codec: remoteVideoData.length && remoteVideoData[0].codec
    }
  }

  const handleVisitIdChange = debounce((value) => {
    getWebRtcStats(value, token)
    setSearched(true)
  }, 800)
  const onChange = e => handleVisitIdChange(e.target.value)

  const createChart = (data, fieldName) => (
    <LineChart
      width={800}
      height={300}
      data={data}
      margin={{
        top: 5,
        right: 30,
        left: 20,
        bottom: 5,
      }}
    >
      <CartesianGrid strokeDasharray="3 3" />
      <XAxis dataKey="formattedTime" />
      <YAxis />
      <Tooltip />
      <Legend margin={{ top: 20, left: 0, right: 0, bottom: 0 }} />
      <Line type="monotone" dataKey={fieldName} stroke="#b31118" />
    </LineChart>
  )

  const renderData = (trackType, mediaType) => {
    const dataKey = `${trackType}_${mediaType}`
    const dataObj = dataByType[dataKey]
    return (
      <>
        <h2 styleName='sectionName'>
          {`${dataKey} TRACK STATS`}&nbsp;
          <span styleName='hideBtn' onClick={() => toggleSection(dataKey)}>
            [{sectionsToShow.includes(dataKey) ? 'Hide' : 'Show'}]
          </span>
        </h2>
        { sectionsToShow.includes(dataKey) && (
          <>
            <div styleName='configure' onClick={() => openConfigureModal(dataKey)}>[ Configure ]</div>
            <div styleName='sectionData'>
              <div styleName='commonDataField'>Date: {date}</div>
              <div styleName='commonDataField'>Peer connection id: {peerConnectionId}</div>
              <div styleName='commonDataField'>Codec: {dataObj.codec}</div>
              {statsToShow[dataKey].map(statName => (
                <div styleName='chart' key={`${dataKey + statName}`}>{createChart(dataObj.data, statName)}</div>
              ))}
            </div>
          </>
        )}
      </>
    )
  }

  const toggleSection = dataKey => {
    if (sectionsToShow.includes(dataKey)) {
      setSectionsToShow(sectionsToShow.filter(s => s !== dataKey))
    } else {
      setSectionsToShow([...sectionsToShow, dataKey])
    }
  }

  const submitLoginForm = () => {
    const basicAuthToken = btoa(`${username}:${password}`)
    // sent request for unexisting visit id just to check credentials are good
    getWebRtcStats(0, basicAuthToken, (status) => {
      if (status !== 401) setToken(basicAuthToken)
    })
  }

  const loginModal = (
    <Modal
      title='Login'
      styleName="loginModal"
      show={!token}
      footer={(
        <Button type='go' onClick={submitLoginForm}>Login</Button>
      )}
    >
      { statusCode === 401 && (
        <div styleName="error">Invalid credentials</div>
      )}
      <Form.Group>
        <Form.Control
          type="text"
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          styleName="usernameInput"
        />
        <Form.Control
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          styleName="passwordInput"
        />
      </Form.Group>
    </Modal>
  )

  const openConfigureModal = (dataKey) => {
    const stats = dataByType[dataKey]
    const selected = statsToShow[dataKey]
    const availableFields = Object.keys(stats.data[0]).filter(st => !STATS_EXCLUSIONS.includes(st))
    setSelectedStats(selected)
    setAvailableStats(availableFields)
    setConfigureModalProps({ dataKey: dataKey, show: true})
  }

  const onCheck = (e) => {
    if (e.target.checked) {
      setSelectedStats([...selectedStats, e.target.name])
    } else {
      setSelectedStats(selectedStats.filter(st => st !== e.target.name))
    }
  }

  const submitConfigureForm = () => {
    setStatsToShow({...statsToShow, [configureModalProps.dataKey]: selectedStats})
    setConfigureModalProps({ dataKey: '', show: false})
  }

  const configureModal = (
    <Modal
      title='Select fields to show'
      styleName="configureModal"
      show={configureModalProps.show}
      footer={(
        <Button type='go' onClick={submitConfigureForm}>Apply</Button>
      )}
    >
      <Form.Group>
        { availableStats.map(stat => (
          <Form.Check
            styleName='checkbox'
            key={stat}
            type='checkbox'
            id={stat}
            label={stat}
            name={stat}
            onChange={onCheck}
            checked={selectedStats.includes(stat)}
          />
        ))}
      </Form.Group>
    </Modal>
  )

  return (
    <div styleName='wrapper'>
      <h1 styleName='header'>WebRTC Stats</h1>
      <div styleName='searchContainer'>
        <Form>
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="Enter visit id for search"
              onChange={onChange}
            />
            { searched && !loading && statusCode === 404 && (
              <div styleName='noResults'>No results</div>
            )}
          </Form.Group>
        </Form>
      </div>
      { loading && (
        <div styleName='loaderContainer'>
          <LoadingSpinner />
        </div>
      )}
      { !loading && statsData && (
        <div styleName='dataContainer'>
          <div styleName='peers'>
            {Object.keys(statsData).map(peerConnectionId => (
              <div
                key={peerConnectionId}
                styleName={cn('peer', { selected: selectedPeerId === peerConnectionId})}
                onClick={() => setSelectedPeerId(peerConnectionId)}
              >
                peer: {peerConnectionId}
              </div>
            ))}
          </div>
          <div styleName='details'>
            {renderData(LOCAL, AUDIO)}
            {renderData(LOCAL, VIDEO)}
            {renderData(REMOTE, AUDIO)}
            {renderData(REMOTE, VIDEO)}
          </div>
        </div>
      )}
      {loginModal}
      {configureModal}
    </div>
  )
}

export default injectIntl(WebRTCStats)
